import produce from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import {
  ApiResponse,
  PaginatedListOfRouterListDto,
  RouterCreateDto,
  RouterEditDto,
  RouterListDto,
  useTrafficBuoyApi,
} from "~/api/GeneratedApi";
import { QueryOptions, QueryResult } from "~/components/GenericTable";
import { CustomQueryKey } from "~/utils/hooks/QueryKeys";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { staleTime30mins, useCustomQuery } from "~/utils/hooks/useCustomQuery";

type UseTrafficBuoyRoutersArgs = {
  onMutationSuccess?: () => void;
};

type UseTrafficBuoyRoutersReturn = {
  routers: RouterListDto[];
  routersLoading: boolean;
  getRouterById: (id: string) => Promise<RouterListDto | null>;
  setBuoyLightsOn: (args: BuoyControlArgs) => void;
  setBuoyLightsOff: (args: BuoyControlArgs) => void;
  getRouters: (q: QueryOptions) => Promise<QueryResult<RouterListDto>>;
  createRouterAsync: (dto: RouterCreateDto) => Promise<ApiResponse>;
  editRouterAsync: (args: EditRouterArgs) => Promise<ApiResponse>;
  deleteRouterAsync: (id: string) => Promise<ApiResponse>;
  handleTrafficBuoyRouterUpdate: (newRouters: RouterListDto[]) => void;
};

type EditRouterArgs = {
  id: string;
  data: RouterEditDto;
};

type BuoyControlArgs = {
  deviceId: string;
};

export const trafficBuoyListQueryKey: CustomQueryKey = "traffic-buoy-routers";

export function useTrafficBuoyRouters({
  onMutationSuccess,
}: UseTrafficBuoyRoutersArgs = {}): UseTrafficBuoyRoutersReturn {
  const {
    getRouters,
    createRouter,
    deleteRouter,
    editRouter,
    buoyControlOn,
    buoyControlOff,
  } = useTrafficBuoyApi();
  const qc = useQueryClient();

  const { data: routersQueryData, isLoading: routersLoading } = useCustomQuery(
    trafficBuoyListQueryKey,
    () => getRouters(),
    {
      staleTime: staleTime30mins,
      select: React.useCallback(
        (d: PaginatedListOfRouterListDto) => d.results,
        [],
      ),
    },
  );

  const { mutate: setBuoyLightsOn } = useCustomMutation(
    ({ deviceId }: BuoyControlArgs) => buoyControlOn(deviceId),
  );
  const { mutate: setBuoyLightsOff } = useCustomMutation(
    ({ deviceId }: BuoyControlArgs) => buoyControlOff(deviceId),
  );

  const { mutateAsync: createRouterAsync } = useCustomMutation(
    (data: RouterCreateDto) => createRouter(data),
    {
      onSuccess: () => {
        onMutationSuccess?.();
        void qc.invalidateQueries(trafficBuoyListQueryKey as string, {
          exact: true,
        });
      },
    },
  );

  const { mutateAsync: editRouterAsync } = useCustomMutation(
    ({ id, data }: EditRouterArgs) => editRouter(data, id),
    {
      onSuccess: () => {
        onMutationSuccess?.();
        void qc.invalidateQueries(trafficBuoyListQueryKey as string, {
          exact: true,
        });
      },
    },
  );

  const { mutateAsync: deleteRouterAsync } = useCustomMutation(
    (id: string) => deleteRouter(id),
    {
      onSuccess: () => {
        onMutationSuccess?.();
        void qc.invalidateQueries(trafficBuoyListQueryKey as string, {
          exact: true,
        });
      },
    },
  );

  const routers = React.useMemo(
    () => routersQueryData ?? ([] as RouterListDto[]),
    [routersQueryData],
  );

  const getRouterById = React.useCallback(
    (id: string) => {
      return Promise.resolve(routers.find((r) => r.deviceId === id) ?? null);
    },
    [routers],
  );

  /**
   * Update the data in the query client
   */
  const handleTrafficBuoyRouterUpdate = React.useCallback(
    (newRouters: RouterListDto[]) => {
      const prevData = qc.getQueryData<PaginatedListOfRouterListDto>(
        trafficBuoyListQueryKey as string,
      );
      if (!prevData) return;
      qc.setQueryData<PaginatedListOfRouterListDto>(
        trafficBuoyListQueryKey as string,
        () => {
          return produce(prevData, (draft) => {
            newRouters.forEach((r) => {
              const index = draft.results.findIndex(
                (x) => x.deviceId === r.deviceId,
              );
              if (index !== -1) {
                // Need to parse dates separately
                draft.results[index] = r;
                draft.results[index].reportedUpdatedDate = new Date(
                  r.reportedUpdatedDate,
                );
                draft.results[index].stateUpdatedDate = new Date(
                  r.stateUpdatedDate,
                );
              }
            });
          });
        },
      );
      onMutationSuccess?.();
    },
    [onMutationSuccess, qc],
  );

  return {
    routers,
    routersLoading,
    setBuoyLightsOn,
    setBuoyLightsOff,
    getRouters,
    getRouterById,
    createRouterAsync,
    editRouterAsync,
    deleteRouterAsync,
    handleTrafficBuoyRouterUpdate,
  };
}
